import * as R from 'ramda';
import { isAssetCustomFieldForInternalEnabled } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/feature-flags';
import { getSelectOptions } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/custom-fields';
import { getFilters } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters';
import {
	NO_CUSTOM_FIELD_VALUE_ID,
	getCustomFieldsWithFilteringAllowed,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/custom-field-filter';
import type { SelectOption } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/select-options/types';
import { CUSTOM_FIELD_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/filters/types';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { createSelector } from '@atlassian/jira-portfolio-3-portfolio/src/common/reselect';
import { CustomFieldTypes } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import type { CustomFieldFilterInfo, QueryProps } from './types';

export const getFilteredCustomFieldsPure = (
	{ [CUSTOM_FIELD_FILTER_ID]: { value } }: ReturnType<typeof getFilters>,
	filterableCustomFields: ReturnType<typeof getCustomFieldsWithFilteringAllowed>,
	selectOptions: ReturnType<typeof getSelectOptions>,
) => {
	const results: Array<CustomFieldFilterInfo> = [];

	filterableCustomFields.forEach((customField) => {
		const fieldKey = customField.type && customField.type.key;
		const customFieldId = String(customField.id);
		const filterValue = isDefined(value[customFieldId]) ? value[customFieldId] : [];
		if (filterValue.length) {
			let customFieldViewInfo: { id: string | number; name: string }[] = [];
			switch (fieldKey) {
				case CustomFieldTypes.Labels:
					customFieldViewInfo = filterValue
						.filter((label: string) => label !== NO_CUSTOM_FIELD_VALUE_ID)
						.map((label: string, i: number) => ({
							id: i,
							name: label,
						}));

					break;
				case CustomFieldTypes.AssetObject:
				case CustomFieldTypes.MultiSelect:
				case CustomFieldTypes.MultiCheckboxes:
				case CustomFieldTypes.RadioButtons:
				case CustomFieldTypes.SingleSelect: {
					if (!isAssetCustomFieldForInternalEnabled()) {
						if (fieldKey === CustomFieldTypes.AssetObject) {
							break;
						}
					}

					const options: Array<SelectOption> = selectOptions.filter(
						(opt) => opt.fieldId === customField.id,
					);
					if (isDefined(options)) {
						customFieldViewInfo = options
							.filter(({ id }) => R.includes(`${id}`, filterValue))
							// eslint-disable-next-line @typescript-eslint/no-shadow
							.map(({ id, value }) => ({
								id,
								name: value,
							}));
					}
					break;
				}
				default:
					break;
			}

			const noValueOption: { id: string | number; name: string }[] = R.contains(
				NO_CUSTOM_FIELD_VALUE_ID,
				filterValue,
			)
				? [
						{
							id: NO_CUSTOM_FIELD_VALUE_ID,
							name: '',
						},
					]
				: [];
			const values = noValueOption.concat(customFieldViewInfo);
			if (values.length) {
				results.push({
					id: customFieldId,
					title: customField.title,
					values,
				});
			}
		}
	});
	return results;
};

const getFilteredCustomFields = createSelector(
	[getFilters, getCustomFieldsWithFilteringAllowed, getSelectOptions],
	getFilteredCustomFieldsPure,
);

export const mapStateToProps = (state: State): QueryProps => ({
	customFields: getFilteredCustomFields(state),
});
